import { z } from 'zod';
import { ulidSchema } from './id';

/**
 * Watching — the contract layer for block **B29**.
 *
 * `BE-29.1` (`FR-COLLAB-022`, watch and unwatch a card), `BE-29.2`
 * (`FR-COLLAB-023`, watch a list or a whole board) and the state half of
 * `FR-COLLAB-024` (what a card membership implies about watching). The
 * *fan-out* — which watcher hears about what, and the eight `watched_*`
 * notification types it writes — is `./notification` plus
 * `apps/server/src/notifications/{watchers,auto-watch,generate}.ts`, and
 * nothing in this file is on that path.
 *
 * ---------------------------------------------------------------------------
 * THE WHOLE CONTRACT IS THREE FIELDS, AND THAT IS THE DESIGN RATHER THAN AN
 * UNFINISHED FILE
 * ---------------------------------------------------------------------------
 * `watches` is an eighteen-migrations-old table (`0002_boards_lists_cards.sql`)
 * with no writer until this block. It has seven columns, and this contract puts
 * **none of them** on the wire verbatim:
 *
 *   - `id` — a watch has no identity a caller can use. Every route addresses
 *     it as *the caller's watch on this target*, which is a
 *     `(user_id, target)` pair the session and the URL already carry. A
 *     `watchId` on the wire would invite `DELETE /watches/:id`, and that route
 *     would need its own permission model to stop one account deleting
 *     another's subscription — a whole second question the singleton
 *     subresource shape does not raise.
 *   - `user_id` — **the session, never a parameter**, per SC-5. It is the same
 *     rule `notificationQuerySchema` enforces by being strict about
 *     `recipientUserId`, and for the same reason: a `userId` a caller could
 *     send is an authorisation decision moved into a request body.
 *   - `card_id` / `list_id` / `board_id` — collapsed into
 *     `targetType` + `targetId`. `ck_watches_target` already guarantees exactly
 *     one of the three is non-null, so three nullable id fields on the wire
 *     would be a shape with **eight** representable states of which one is
 *     legal, re-encoding a CHECK the database enforces anyway.
 *   - `created_at` / `updated_at` — nothing renders when a subscription
 *     started. `FR-COLLAB-022` describes a toggle, and a toggle has one bit of
 *     state.
 *
 * So the answer to *am I watching this* is a boolean, and the two fields beside
 * it exist to say **what** it is an answer about — see `watchStateSchema`.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ (a) THIS IS A NEW SHAPE, NOT A `watching` FIELD ON `cardDetailSchema`,
 * AND THE OBVIOUS ALTERNATIVE IS REFUSED FOR A REASON THAT IS NOT TASTE
 * ---------------------------------------------------------------------------
 * `CLAUDE.md`: *extend a shipped schema on a copy, never widen it in place —
 * one schema serialises several routes, so widening changes responses that must
 * not carry the field.* `cardDetailSchema` is the sharpest instance of that
 * rule in the package: it serialises `GET /cards/:id`, which is
 * **anonymous-capable** on a public board (`requireCardReader` takes
 * `userId: string | null`). A per-viewer `watching` field has no truthful value
 * for a caller with no account — `false` would be a lie ("you are not watching
 * this", when there is no *you*), and `null` would put a third state on a
 * boolean that `FR-COLLAB-022` gives two.
 *
 * The consequence, stated because it is the half a client author discovers
 * later: **the card back needs a second request.** `GET /cards/:id` answers
 * what the card is, `GET /cards/:id/watch` answers what this viewer's
 * relationship to it is, and the second one is behind `requireUser` where the
 * first is not. That is one round trip bought for one field, and it is the
 * price of the card read staying anonymous-capable at all.
 *
 * ⚠⚠ **THE PARAGRAPH THAT STOOD HERE ALSO REFUSED `boardFaceCardSchema`, AND
 * THAT HALF OF THE RULING IS NOW RELEASED — the card-back half above STANDS
 * and is untouched.** B29 wrote: *"a watch flag on the board face would be a
 * per-viewer field on the response every board open reads, and the product
 * tie-breaker settles it before the anonymous argument has to: a watch is
 * invisible to other members by requirement, so it is not board-face
 * information."* It is kept here verbatim rather than deleted, because the
 * next reader has to be able to see **why it moved** rather than find it
 * silently gone. Two facts, both from the corpus, say the step from
 * *invisible to other members* to *absent from the board face* was wrong:
 *
 *   1. **`FR-BOARD-030` puts the eye on the card front in its own behaviour
 *      text and in AC4.** Behaviour: *"a compact strip of badges … a **watch
 *      (eye) indicator when the viewer is watching the card**, and member
 *      avatars aligned to the right."* AC4: *"Given a card the viewer
 *      watches, when it renders, then an eye indicator is shown **to that
 *      viewer only**."* `FR-COLLAB-022` — this block's own requirement —
 *      agrees in as many words: *"An eye icon on the card back **(and on the
 *      card front)** indicates the watching state."* So *invisible to other
 *      members* means **per-viewer**, and rendering it is required by name
 *      rather than traded against the tie-breaker: removing it fails an
 *      acceptance criterion rather than winning a readability argument. It
 *      also costs nothing on a card that already shows any badge (it joins
 *      the existing strip as one icon) and renders at all only when `true`.
 *   2. **A per-viewer, viewer-gated, nullable field already ships on that
 *      exact shape.** `boardFaceCardSchema.voteCount` (B59, *after* B29) is
 *      `null` for any caller who may not see tallies, including every
 *      anonymous one. The categorical claim that the board face carries no
 *      per-viewer field was already false.
 *
 * The shape is therefore `boardFaceCardSchema.watching: z.boolean().nullable()`
 * — `null` for a caller with no session — which is also the direct answer to
 * this file's own objection two paragraphs up (*"`null` would put a third state
 * on a boolean `FR-COLLAB-022` gives two"*). That objection is what still keeps
 * `cardDetailSchema` clean: there, a second request (`GET /cards/:id/watch`,
 * behind `requireUser`) is available and cheap, so the two-state boolean is
 * affordable. On the board face there is no second request available at all —
 * the board's card components receive board-face data only through the card
 * they are handed — so the choice is a three-state field or no answer, and
 * `voteCount` had already settled which of those this shape uses.
 *
 * ⚠ **What the board-face field is NOT.** It reports **card-scoped `watches`
 * rows only** — a list watch or a board watch does not light it — which is
 * `watchStateSchema.targetType`'s own ruling below, applied rather than
 * re-decided. And it is not a second `watchStateSchema`: this file still
 * declares exactly one shape, and the nine routes still all answer it.
 *
 * ---------------------------------------------------------------------------
 * ⚠ (b) WHY THERE IS NO "MY WATCHES" LISTING SHAPE HERE
 * ---------------------------------------------------------------------------
 * `./notification`'s header states the rule this file obeys: *a schema written
 * "while we are in the file" against no route is how `BE-02.1` quietly becomes
 * unreviewable.* B29 builds nine routes and every one of them answers
 * `watchStateSchema`. No requirement asks for "everything I watch" as a page —
 * `FR-COLLAB-022` and `FR-COLLAB-023` are both about one target at a time —
 * so a `watchListSchema` here would be a paged shape with no reader, no cursor
 * decision made, and no acceptance criterion to check it against.
 *
 * ⚠ **And there is no params schema**, for the same reason `./notification`
 * declares none: `z.strictObject({ id: ulidSchema })` lives **at the route
 * module**, beside `commentParams`, `labelParams` and `cardParams`. Exporting
 * one from here would be the first of its kind and would put a route's URL
 * shape in a package the browser also imports.
 *
 * ⚠ **And there is no request body anywhere in this block.** `PUT
 * /cards/:id/watch` and `DELETE /cards/:id/watch` carry the whole gesture in
 * the method — the design's argument for `PUT` over `POST` is that a watch is a
 * per-user **singleton subresource** whose creation must be idempotent, and a
 * body carrying `{ watching: boolean }` would collapse the two methods into one
 * route that can be asked to do the opposite of what it is named. If a body
 * ever arrives here it will be `z.strictObject`, like every other input in this
 * package; today the absence is the contract.
 */

// ---------------------------------------------------------------------------
// The vocabulary.
// ---------------------------------------------------------------------------

/**
 * What a watch can be *on*.
 *
 * These are not column values — `watches` has no `target_type` column, and
 * `ck_watches_target` models the same three-way choice as three nullable ids
 * with exactly one non-null. This tuple is the **wire** spelling of that
 * choice, so a client has one field to switch on instead of three nullable ones
 * to test in order.
 *
 * ⚠ **The order is NARROWEST FIRST, and it is load-bearing beyond
 * documentation.** `FR-COLLAB-023` scopes a list watch to every card in the
 * list and a board watch to every card on the board, so the three arms nest:
 * card ⊂ list ⊂ board. The fan-out's `card.created` rule turns on exactly that
 * ordering — a user watching both a list and its board gets **one**
 * `watched_card_created_in_list`, the narrowest scope that matched, never two
 * notifications and never the board-scoped name. Reordering this tuple
 * alphabetically would leave every route working and would silently invert
 * which of the two `watched_card_created_*` types a dual watcher receives, with
 * nothing red anywhere.
 *
 * ⚠ **A `const` TUPLE, and emphatically NOT `Object.keys(…) as [string,
 * ...string[]]`** — `NOTIFICATION_TYPE_NAMES`' argument, which applies to every
 * closed vocabulary in this package. That spelling infers
 * `WatchTargetType = string`, and the value that would then compile is the one
 * a route uses to pick which of the three columns to write.
 */
export const WATCH_TARGET_TYPES = ['card', 'list', 'board'] as const;

/**
 * `z.enum` over the vocabulary.
 *
 * ⚠ **This one is genuinely different from `notificationTypeSchema` and
 * `labelColourSchema`, which it otherwise resembles**: those two guard a
 * CHECK-constrained `varchar`, so an out-of-vocabulary value means the schema
 * and the constraint have drifted. There is no `ck_watches_target_type` to
 * drift from — this vocabulary is **derived** from which of three columns the
 * row filled in, by the route that read it. So a refusal here does not mean a
 * stale registry; it means a serialiser produced a target type from something
 * other than the three columns, which is a bug in one file rather than a
 * migration nobody ran.
 */
export const watchTargetTypeSchema = z.enum(WATCH_TARGET_TYPES);

export type WatchTargetType = z.infer<typeof watchTargetTypeSchema>;

// ---------------------------------------------------------------------------
// The one shape.
// ---------------------------------------------------------------------------

/**
 * What every watch route answers — all nine of them, in both directions.
 *
 * `GET`, `PUT` and `DELETE` on `/cards/:id/watch`, `/lists/:id/watch` and
 * `/boards/:id/watch` all return this, and the mutations return it as a
 * **re-read of the state they just established**, never as an echo of the
 * request. That is `labelSchema`'s and `commentSchema`'s call, restated: one
 * view for the read and the write, because they answer the same question —
 * *what is this now* — and a narrower body for the mutation would let the two
 * drift.
 *
 * ⚠ **`watching` is `false` on a successful `DELETE`, and `DELETE` on a watch
 * that was never there is a 200 carrying exactly the same body.** The client's
 * intent — *I am not watching this* — is already true, so there is nothing to
 * report as an error; a 404 would make an unwatch button fail the second time
 * it is pressed. The schema is what makes that expressible: a 204 with no body
 * would have nowhere to say what the state now is, and the client would have to
 * assume rather than read.
 *
 * ⚠ **`targetType` and `targetId` are on the wire although the caller supplied
 * both in the URL, and that is deliberate rather than redundant.** The card
 * back fires `GET /cards/:id/watch` and a board header fires
 * `GET /boards/:id/watch`; a client holding several of these in flight —
 * TanStack Query is in `apps/web`'s dependencies precisely to hold several —
 * needs each response to identify itself. A bare `{ watching: boolean }` is a
 * shape that cannot be told apart from another target's answer once it has left
 * the call that requested it. `./cross-board-move`'s rule about not
 * re-encoding a fact the row already carries does not reach this: the *row*
 * carries neither field, and the request is not the row.
 *
 * ⚠ **`z.object`, not `z.strictObject`** — this is a view, and views in this
 * package are `z.object` (Zod 4 **strips** unknown keys rather than refusing
 * them). Only the input schemas are strict, because there a typo'd key must be
 * a 422. A test asserting `safeParse({…, extra}).success === false` here would
 * be asserting Zod's stripping rather than this shape; `watch.test.ts` asserts
 * `Object.keys(shape)` instead, which is `notification.test.ts`'s finding
 * applied rather than rediscovered.
 */
export const watchStateSchema = z.object({
  /**
   * Which of `ck_watches_target`'s three arms this answer is about — and, for
   * a `watching: false` answer, which arm the *absent* row would have used.
   *
   * ⚠ **It is the type of the target the route was called on, NOT "the arm
   * that made `watching` true".** A user watching a board is not watching each
   * card on it: `GET /cards/:id/watch` on a card whose board they watch answers
   * `{ targetType: 'card', watching: false }`, because there is no card-scoped
   * row and `PUT /cards/:id/watch` would create one. Answering `'board'` here
   * would make the card's unwatch button appear to control a board-wide
   * subscription it cannot reach. **The fan-out and this field ask different
   * questions** — *who hears about this event* unions all three arms
   * (`watchers.ts`), and *what did I subscribe to* is per-row — and conflating
   * them is the one misreading of this contract that produces a plausible,
   * silently wrong UI.
   */
  targetType: watchTargetTypeSchema,
  /**
   * The card, list or board the answer is about — `watches.card_id`,
   * `list_id` or `board_id` depending on `targetType`, and the `:id` path
   * segment the route was called with either way.
   *
   * `ulidSchema` rather than a bare bounded string: unlike a title or a short
   * link this is an **id the client sends back**, so the strict Crockford
   * base32 form is the right one — the same call `./notification`'s refs make
   * for their `id` fields. Case matters (`./id`'s note: Postgres compares text
   * case-sensitively under any deterministic collation, and every id column
   * here is `char(26) COLLATE "C"`).
   */
  targetId: ulidSchema,
  /**
   * `true` when a `watches` row exists for `(this session's user, this
   * target)`.
   *
   * ⚠ **The row's PRESENCE is the whole state — there is no `suppressed` or
   * `explicit` flag on the table and there must not be one.**
   * `FR-COLLAB-024`'s fourth acceptance criterion is *"a user who explicitly
   * unwatched and is later added as a card member has watching re-enabled"*,
   * and a flag would **break** it rather than serve it: an explicit unwatch
   * would set the flag, and the later `member.added` would have to *remember*
   * to clear it. Delete-then-upsert has no such failure mode. Recorded here, in
   * the contract, because this is where a later block will look for somewhere
   * to put "watching but muted" and the answer is that the two states are one.
   *
   * ⚠ **`false` does not mean "hears nothing about this card".** Three other
   * mechanisms notify without a `watches` row — a resolved `@mention`
   * (`FR-NOTIF-002`'s third AC states it explicitly: not watching, not a
   * member, mentioned, still notified), card membership via the
   * `payloadTarget` audience, and B24's due reminders, whose recipient set is
   * `card_members ∪ watches`. A client rendering this boolean as "you will be
   * notified about this card" is rendering a claim the field does not make.
   */
  watching: z.boolean(),
});

export type WatchState = z.infer<typeof watchStateSchema>;
